/**
 * V4 FR-2.5 (2026-05-27) — Tipos para el informe PDF individual de un
 * SurveillanceRoute. Separados de `DetailedPDFRecord` (Action/Accident)
 * porque la estructura del dominio RV es lo suficientemente distinta
 * (tramos como sub-tabla, sin sketches/budget/materials/etc., con campos
 * propios como concesión/equipo/carretera resueltos) para que mezclarlas
 * en un único tipo sería ruido. Sigue el patrón "una shape por entidad
 * principal" del proyecto.
 *
 * 2026-07-27 (informe por niveles): añade el contrato de opciones y la shape
 * completa del documento (`SurveillanceReportData`).
 */

import { DetailedPDFRecord } from './detailedTypes';
import type { ReportImage } from './reportImages';

/**
 * Nivel de detalle de las actuaciones asociadas en el informe del RV
 * (2026-07-27). `list`/`list-photos` NO llaman a `fetchActionRecord` (~10
 * queries por actuación): solo Order por id + imágenes. El coste alto se
 * paga únicamente en `full`.
 */
export type SurveillanceChildrenMode = 'none' | 'list' | 'list-photos' | 'full';

/**
 * Qué se pinta en el informe. Se elige en el modal (presets + casillas) y
 * viaja hasta el builder y el documento — nadie más decide qué entra.
 */
export type SurveillanceReportOptions = {
  /** Tabla de tramos con las columnas que se ven en pantalla (`SegmentList`). */
  segmentsTable: boolean;
  /** Añade Carril y ROID del tramo: datos reales que la pantalla no muestra. */
  segmentExtraColumns: boolean;
  /** Agregado por causa de pausa: nº de tramos, duración y km. */
  causeSummary: boolean;
  children: SurveillanceChildrenMode;
  /** Anexo con las fotos de cada tramo (cuelgan de la Order gemela del tramo). */
  segmentPhotos: boolean;
  /**
   * Órdenes vinculadas al recorrido (cross-link 3, 2026-09-22): ficha de la OT,
   * primera comunicación con fotos y los trabajos del equipo del recorrido en
   * la ventana del turno (D8). Mismos niveles que `children`.
   */
  linkedOrders: SurveillanceChildrenMode;
};

/** Por defecto: la ficha del recorrido + lo que se ve en la tabla de tramos. */
export const SR_REPORT_PRESET_SUMMARY: SurveillanceReportOptions = {
  segmentsTable: true,
  segmentExtraColumns: false,
  causeSummary: false,
  children: 'none',
  segmentPhotos: false,
  linkedOrders: 'none',
};

/** "Como los demás informes": todo, con el detalle y las fotos de las hijas. */
export const SR_REPORT_PRESET_FULL: SurveillanceReportOptions = {
  segmentsTable: true,
  segmentExtraColumns: true,
  causeSummary: true,
  children: 'full',
  segmentPhotos: true,
  linkedOrders: 'full',
};

/**
 * Fila de la tabla de tramos del informe. Formateada con LOS MISMOS helpers
 * que `SegmentList` (la tabla de pantalla) — ver `surveillanceReportRows.ts`.
 * Los tres últimos campos son crudos, solo para agregar.
 */
export type SurveillanceSegmentRow = {
  /** Posición cronológica 1..N — lo que pinta la columna "Nº" de la pantalla. */
  index: number;
  /** ROID del tramo (`Tr.NNNNNNN.NNNN.01`) — solo en las columnas extra. */
  roid: string;
  /**
   * Carretera del tramo (multi-carretera 2026-08-15). Solo presente cuando el
   * recorrido tiene MÁS DE UNA: un PK a secas no identifica un sitio si una
   * troncal y su ramal comparten kilometraje sobre trazados que se separan
   * kilómetros. Con una sola carretera se omite, y el informe queda idéntico.
   */
  road?: string;
  /** Sentido traducido, sin repetir la palabra de la cabecera. */
  direction: string;
  /** Carril traducido (`right` → "Dret") o `-` si no se informó. */
  lane: string;
  pkInit: string;
  /** `-` mientras el tramo sigue abierto (aún no tiene PK final). */
  pkEnd: string;
  /** Distancia del tramo en km con un decimal, o `—` si falta algún PK. */
  km: string;
  /** Hora de inicio `HH:mm` (igual que la pantalla). */
  startTime: string;
  /** Hora final `HH:mm`, o `-` si el tramo sigue abierto. */
  endTime: string;
  /** Duración `Xh Ym`, o `—` si el tramo sigue abierto. */
  duration: string;
  /** Causa de la parada traducida, o `-` si no la hay. */
  pauseCause: string;
  /** Detalle de la causa (columna "Descripción" de la pantalla). */
  description: string;
  /**
   * ROID de la actuación básica vinculada; sin ella, los trabajos de la OT del
   * aviso («De la OT · X», uno por línea, 30-oct); si no, `-`.
   */
  linkedBasicActionRoid: string;
  /** ROID de la OT de sala vinculada, o `-`. */
  linkedExternalRoid: string;
  /** Id de la Order de la básica vinculada — cruza el tramo con la hija. */
  linkedBasicActionOrderId: string;
  // ── Crudos para los agregados ──
  causeKey: string | null;
  /** 0 para el tramo abierto: no se inventa un elapsed en un documento. */
  durationMs: number;
  meters: number;
};

/** Fila del agregado "Resumen por causa de pausa". */
export type SurveillanceCauseRow = {
  causeKey: string;
  cause: string;
  count: number;
  duration: string;
  km: string;
};

/** Fila de un trabajo (actuación o accidente) de una OT vinculada, nivel `list`. */
export type LinkedWorkRow = {
  roid: string;
  type: string;
  time: string;
  state: string;
  registeredBy: string;
  description: string;
};

/**
 * Bloque de una OT vinculada al recorrido (cross-link 3): ficha, primera
 * comunicación y los trabajos del equipo del recorrido dentro del turno.
 */
export type LinkedOrderReportBlock = {
  orderId: string;
  roid: string;
  title: string;
  /** Ficha de la OT: pares label/valor para `DataGrid` (tipo, estado, descripción, procedencia, tramos…). */
  info: { label: string; value: string }[];
  /** Primera comunicación de la OT (por fecha de registro), renderizada como un registro más; `null` si no hay. */
  firstCommunication: DetailedPDFRecord | null;
  /** Trabajos de la patrulla (`workOrderIds` del back) en nivel `list` / `list-photos`. */
  workRows: LinkedWorkRow[];
  /** Fotos de los trabajos, nivel `list-photos`. */
  workPhotos: { title: string; images: { url: string; name: string }[] }[];
  /** Trabajos con detalle completo, nivel `full`. */
  workRecords: DetailedPDFRecord[];
  /** Actuaciones y accidentes de la OT que NO son de esta patrulla en este turno. */
  otherWorksCount: number;
};

/**
 * Datos completos del informe de un RV, ya formateados para pintar. Lo compone
 * `fetchSurveillanceReportData` y lo consume `SurveillanceReport.tsx`: el
 * documento no vuelve a decidir nada, solo renderiza lo que hay según
 * `options`.
 */
export type SurveillanceReportData = {
  /** ROID canónico del RV (`Su.NNNNNNN.NNNN.02`). */
  roid: string;
  /** Id de la Order doble del RV (`orderIDs[0]`). */
  parentOrderId: string;
  /** Estado ya mapeado a variante de `SpecialTag` (in-progress → …). */
  state: string;
  /** Nombre del recorrido; cadena vacía en RVs legacy sin nombre. */
  name: string;
  /** Título del documento (portada, cabeceras y pie). */
  title: string;
  generatedAt: string;
  /** Ficha del recorrido: pares label/valor listos para `DataGrid`. */
  basicInfo: { label: string; value: string }[];
  description: string;
  /** El wizard del RV no tiene campo Notas, pero la API lo acepta: si hay, se pinta. */
  notes?: string;
  /** Fotos propias de la Order del RV — hoy no hay UI que las suba; si hay, se pintan. */
  images: ReportImage[];
  /** Tripulación: operario, matrícula y conductor. */
  crew: { operator: string; vehicle: string; driver: string }[];
  segments: SurveillanceSegmentRow[];
  /** Vacío si no se pidió el agregado por causa. */
  causeSummary: SurveillanceCauseRow[];
  /** Niveles `list` / `list-photos`: una fila por actuación asociada. */
  childRows: {
    roid: string;
    time: string;
    /** Nº del tramo que la originó, o `-` si no está vinculada. */
    segment: string;
    state: string;
    description: string;
  }[];
  /** Nivel `list-photos`: fotos agrupadas por actuación, con su pie. */
  childPhotos: { title: string; images: ReportImage[] }[];
  /** Nivel `full`: páginas de detalle (mismo render que el resto de informes). */
  childRecords: DetailedPDFRecord[];
  /** Anexo de fotos por tramo (solo tramos con fotos), en orden cronológico. */
  segmentPhotos: { title: string; images: { url: string; name: string }[] }[];
  /** Una entrada por OT vinculada al recorrido, en orden de vínculo. */
  linkedOrders: LinkedOrderReportBlock[];
  counts: {
    segments: number;
    actions: number;
    linkedOrders: number;
    linkedWorks: number;
    segmentPhotos: number;
  };
  /** Opciones con las que se generó — el documento decide qué pintar con esto. */
  options: SurveillanceReportOptions;
};
